'use client';

import React, { useState } from 'react';
import { useUser } from '@clerk/nextjs';
import { Loader2, Pencil, CircleCheck, CircleX } from 'lucide-react';
import { toast } from 'sonner';

import { 
  useGetUserProfileQuery, 
  useUpdateUserProfileMutation, 
  useListAirportsMinimalQuery,
  DistanceUnit,
  AirportSortField,
  SortOrder 
} from '@/gql/operations';
// Import the query documents to ensure codegen picks them up
import '@/lib/graphql/queries';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Combobox, ComboboxOption } from '@/components/ui-custom/combobox';

// Real airport search using GraphQL
const useAirportSearch = (query: string) => {
  const { data, loading } = useListAirportsMinimalQuery({
    variables: {
      filter: { search: query },
      sort: { field: AirportSortField.Name, order: SortOrder.Asc },
      pagination: { page: 1, limit: 10 },
    },
    skip: query.length < 2, // Only search if query is at least 2 characters
  });

  const airports = React.useMemo(() => {
    if (!data?.airports?.edges) return [];
    
    return data.airports.edges.map(edge => ({
      id: edge.node.id,
      name: edge.node.name || '',
      iataCode: edge.node.iataCode,
      icaoCode: edge.node.icaoCode,
    }));
  }, [data]);

  return { airports, loading };
};

// Hook to load a specific airport by searching for its codes
const useSelectedAirport = (airportId?: string) => {
  // We'll search for the airport using common codes like "SFO" to find it
  // This is a workaround since we don't have a direct airport-by-ID query
  const { data: profileData } = useGetUserProfileQuery();
  
  const selectedAirport = React.useMemo(() => {
    if (!airportId || !profileData?.userProfile?.homeAirport) return null;
    
    const homeAirport = profileData.userProfile.homeAirport;
    if (homeAirport.id === airportId) {
      return {
        id: homeAirport.id,
        name: homeAirport.name || '',
        iataCode: homeAirport.iataCode,
        icaoCode: homeAirport.icaoCode,
      };
    }
    
    return null;
  }, [airportId, profileData]);

  return selectedAirport;
};

// TODO: Refactor DistanceUnitField and AirportField into a reusable InlineEditField component
// Both components share similar patterns and could be consolidated to reduce code duplication
// while maintaining type safety for different field types (select, combobox, etc.)

// Inline editable distance unit field
function DistanceUnitField({ 
  value, 
  onSave 
}: {
  value: DistanceUnit;
  onSave: (value: DistanceUnit) => Promise<void>;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (selectedValue === value) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(selectedValue);
      setIsEditing(false);
    } catch {
      // Reset to original value on error
      setSelectedValue(value);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setSelectedValue(value);
    setIsEditing(false);
  };

  const getDisplayValue = (unit: DistanceUnit) => {
    switch (unit) {
      case DistanceUnit.Miles:
        return 'Miles';
      case DistanceUnit.NauticalMiles:
        return 'Nautical Miles';
      case DistanceUnit.Kilometers:
        return 'Kilometers';
      default:
        return 'Unknown';
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-2 min-h-[2.5rem] w-full max-w-[320px]">
        <div className="flex-1 min-w-0">
          <Select value={selectedValue} onValueChange={(value) => setSelectedValue(value as DistanceUnit)}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={DistanceUnit.Miles}>Miles</SelectItem>
              <SelectItem value={DistanceUnit.NauticalMiles}>Nautical Miles</SelectItem>
              <SelectItem value={DistanceUnit.Kilometers}>Kilometers</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-1 shrink-0">
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={handleSave}
            disabled={isSaving}
            className="p-2"
          >
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CircleCheck className="h-4 w-4" />}
          </Button>
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={handleCancel}
            disabled={isSaving}
            className="p-2"
          >
            <CircleX className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 min-h-[2.5rem] w-full max-w-[320px]">
      <span className="text-sm flex-1 min-w-0">{getDisplayValue(value)}</span>
      <Button 
        size="sm" 
        variant="ghost" 
        onClick={() => setIsEditing(true)}
        className="p-2 shrink-0"
      >
        <Pencil className="h-4 w-4" />
      </Button>
    </div>
  );
}

// Inline editable airport field
function AirportField({ 
  airportId, 
  onSave 
}: {
  airportId?: string;
  onSave: (airportId?: string) => Promise<void>;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedAirportId, setSelectedAirportId] = useState(airportId);
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  const { airports, loading } = useAirportSearch(searchQuery);
  const currentAirport = useSelectedAirport(airportId);

  const handleSave = async () => {
    if (selectedAirportId === airportId) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(selectedAirportId);
      setIsEditing(false);
    } catch {
      // Reset to original value on error
      setSelectedAirportId(airportId);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setSelectedAirportId(airportId);
    setIsEditing(false);
    setSearchQuery('');
  };

  const displayValue = currentAirport 
    ? `${currentAirport.name} (${currentAirport.iataCode || currentAirport.icaoCode})`
    : 'Not set';

  // Convert airports to combobox options
  const airportOptions: ComboboxOption[] = airports.map(airport => ({
    value: airport.id,
    label: `${airport.name} (${airport.iataCode || '-'} / ${airport.icaoCode || '-'})`,
    searchValue: `${airport.name} ${airport.iataCode || ''} ${airport.icaoCode || ''}`.toLowerCase(),
  }));

  if (isEditing) {
    return (
      <div className="flex items-center gap-2 min-h-[2.5rem] w-full max-w-[320px]">
        <div className="flex-1 min-w-0 overflow-hidden">
          <Combobox
            options={airportOptions}
            value={selectedAirportId || ''}
            onValueChange={(value) => setSelectedAirportId(value || undefined)}
            placeholder="Select airport..."
            searchPlaceholder="Search airports..."
            emptyMessage={loading ? "Loading..." : "No airports found."}
            onSearchChange={setSearchQuery}
            title="Select Airport"
          />
        </div>
        <div className="flex gap-1 shrink-0">
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={handleSave}
            disabled={isSaving}
            className="p-2"
          >
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CircleCheck className="h-4 w-4" />}
          </Button>
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={handleCancel}
            disabled={isSaving}
            className="p-2"
          >
            <CircleX className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 min-h-[2.5rem] w-full max-w-[320px]">
      <span className="text-sm flex-1 min-w-0 truncate">{displayValue}</span>
      <Button 
        size="sm" 
        variant="ghost" 
        onClick={() => setIsEditing(true)}
        className="p-2 shrink-0"
      >
        <Pencil className="h-4 w-4" />
      </Button>
    </div>
  );
}

interface ProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ProfileDialog({ open, onOpenChange }: ProfileDialogProps) {
  const { user } = useUser();
  const { data: profileData, loading: profileLoading, error: profileError } = useGetUserProfileQuery();
  const [updateProfile] = useUpdateUserProfileMutation();

  const handleDistanceUnitSave = async (distanceUnit: DistanceUnit) => {
    try {
      await updateProfile({
        variables: {
          input: {
            distanceUnit,
            homeAirportId: profileData?.userProfile?.homeAirport?.id || null,
          },
        },
        refetchQueries: ['GetUserProfile'],
      });
      toast.success('Distance unit updated successfully!');
    } catch (error) {
      toast.error('Failed to update distance unit. Please try again.');
      console.error('Distance unit update error:', error);
      throw error;
    }
  };

  const handleHomeAirportSave = async (homeAirportId?: string) => {
    try {
      await updateProfile({
        variables: {
          input: {
            distanceUnit: profileData?.userProfile?.distanceUnit || DistanceUnit.Miles,
            homeAirportId: homeAirportId || null,
          },
        },
        refetchQueries: ['GetUserProfile'],
      });
      toast.success('Home airport updated successfully!');
    } catch (error) {
      toast.error('Failed to update home airport. Please try again.');
      console.error('Home airport update error:', error);
      throw error;
    }
  };

  const profile = profileData?.userProfile;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Profile Settings</DialogTitle>
        </DialogHeader>

        {profileLoading && (
          <div className="space-y-6">
            <div className="space-y-4">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          </div>
        )}

        {profileError && (
          <div className="text-red-600 py-4">
            Error loading profile: {profileError.message}
          </div>
        )}

        {!profileLoading && !profileError && (
          <div className="space-y-6">
            {/* Account Information Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Account Information</CardTitle>
                  </div>
                  {profile?.admin && (
                    <Badge variant="secondary">Administrator</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Full Name</label>
                    <Input 
                      value={user?.fullName || 'Not set'} 
                      disabled 
                      className="mt-1"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Update in your account settings
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Email</label>
                    <Input 
                      value={user?.emailAddresses[0]?.emailAddress || 'Not set'} 
                      disabled 
                      className="mt-1"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Update in your account settings
                    </p>
                  </div>
                </div>
                {profile?.createdAt && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Member Since</label>
                    <p className="text-sm mt-1">
                      {new Date(profile.createdAt).toLocaleDateString('en-US', { 
                        month: 'long', 
                        year: 'numeric' 
                      })}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Preferences Card */}
            <Card>
              <CardHeader>
                <CardTitle>Preferences</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6 overflow-hidden">
                <div className="w-full">
                  <label className="text-sm font-medium text-muted-foreground">Distance Unit</label>
                  <div className="mt-2 w-full">
                    <DistanceUnitField 
                      value={profile?.distanceUnit || DistanceUnit.Miles}
                      onSave={handleDistanceUnitSave}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Choose your preferred unit for displaying distances.
                  </p>
                </div>

                <div className="w-full">
                  <label className="text-sm font-medium text-muted-foreground">Home Airport</label>
                  <div className="mt-2 w-full max-w-full overflow-hidden">
                    <AirportField 
                      airportId={profile?.homeAirport?.id}
                      onSave={handleHomeAirportSave}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Set your primary airport for personalized flight information.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
} 